import React, { FC, lazy, Suspense, useMemo } from 'react';
import { IRetailAppProps } from '@ctl/cool-remote';
import { TRemoteBuildAndPriceProps } from './types/remoteBuildAndPrice.types';
import { BuildAndPriceLinkModel } from './models/remoteBuildAndPrice.model';
import MockRetailConfigurator from '../@shared/mockRetailConfigurator';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import { googleDataLayerPush } from '../../utils/analytics';
import { windowService } from '../../utils/window.utils';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import '@googlemaps/react-wrapper';
import 'react-dom/client';
import 'react-redux';
import './styles.scss';

/*  react-dom/client, @googlemaps/react-wrapper, react-redux should be imported along with ReatilConfigurator even if they are not used, 
    since they are shared dependencies of @ctl/cool-remote/app/RetailConfigurator. Refer config-overide for list of shared dependencies 
    Note: To check is the required dependencies are shared, try running the command __federation_shared__ in the browser console where the RetailConfigurator is loading
*/

const RemoteBuildAndPrice: FC<TRemoteBuildAndPriceProps> = (props: TRemoteBuildAndPriceProps) => {
  const model = new BuildAndPriceLinkModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const RetailConfigurator: FC<IRetailAppProps> = lazy(() => import('@ctl/cool-remote/app/RetailConfigurator'));
  const pageQueryUrl = windowService().location?.search;

  const { preSelectedLine, preSelectedSeries, preSelectedModel } = useMemo(() => {
    let preSelectedLine = '';
    let preSelectedSeries = '';
    let preSelectedModel = '';
    if (pageQueryUrl) {
      const searchParams = new URLSearchParams(pageQueryUrl);
      preSelectedLine = searchParams.get('line') || '';
      preSelectedSeries = searchParams.get('series') || '';
      preSelectedModel = searchParams.get('model') || '';
    }
    return { preSelectedLine, preSelectedSeries, preSelectedModel };

  }, [pageQueryUrl, props])

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  return (
    <section className='remote-build-price'>
      {/* RetailConfigurator is a remote component imported by config-override.js using ModuleFederationPlugin */}
      {/* During SSR the component value is mocked with @shared MockRetailConfigurator through server.webpack.config.js */}
      <Suspense fallback={<MockRetailConfigurator />}>
        <RetailConfigurator
          auth={{
            basePath: model.basePath,
            division: model.division,
            vkorg: model.vkorg,
            language: model.language,
            locale: model.locale,
            sapNumber: model.sapNumber,
            ...(model.vhsar ? { vhsar: model.vhsar } : { geographicArea: model.geographicArea })
          }}
          bootstrap={{
            initWith: {
              gammaName: preSelectedLine,
              rangeName: preSelectedSeries,
              productName: preSelectedModel
            }
          }}
          // ErrorCp={() => <div style={{color: 'red'}}>Custom Error Message</div>} // overrides the default error message i.e. "".
          trackFn={(event, data) => !model.disableDLM && googleDataLayerPush({ event, ...data })}
        />
      </Suspense>
    </section>
  )
}

export default RemoteBuildAndPrice;
